import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { Botao, Cartao, Esqueleto, TituloSecao } from '@/componentes/ui'
import { PassosAndroidEstatico, PassosIOS, AvisoDesktop } from '@/funcionalidades/instalacao/instrucoes'
import { formatarData, formatarHorario } from '@/lib/datas'
import { comprimirImagem } from '@/lib/imagens'
import { detectarPlataforma, estaInstalado, permissaoAtual, type Plataforma } from '@/lib/instalacao'
import { ativarNotificacoes, desativarNotificacoes } from '@/lib/push'
import { CHAVES, type ContextoPrograma, usePerfil } from '@/lib/sessao'
import { sair, supabase, urlAssinada } from '@/lib/supabase'
import { cn, mensagemDeErro } from '@/lib/utilitarios'
import type { Foto, LembreteComModelo, Medida } from '@/tipos/tabelas'

/** Perfil (seção 13) e ajustes dos lembretes. */
export function PaginaPerfil({ contexto }: { contexto: ContextoPrograma }) {
  const { data: perfil } = usePerfil()
  const { matricula, dia, duracaoDias } = contexto

  const { data: medidas = [] } = useQuery({
    queryKey: CHAVES.medidas(matricula.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('medidas')
        .select('*')
        .eq('matricula_id', matricula.id)
        .order('registrado_em')

      if (error) throw error
      return data as Medida[]
    },
  })

  const primeira = medidas[0]
  const ultima = medidas[medidas.length - 1]

  return (
    <div className="flex flex-col gap-6 px-5 pt-6 pb-28">
      <header className="flex items-center gap-4">
        <div className="flex size-16 items-center justify-center rounded-full bg-coral/15 text-2xl">
          {perfil?.foto_url ? (
            <img src={perfil.foto_url} alt="" className="size-full rounded-full object-cover" />
          ) : (
            '🌺'
          )}
        </div>
        <div className="min-w-0">
          <h1 className="truncate font-titulo text-xl font-bold">{perfil?.nome || 'Você'}</h1>
          <p className="text-sm text-[var(--tinta-suave)]">
            {matricula.turma.nome} · desde {formatarData(matricula.data_inicio)}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3">
        <Indicador rotulo="Dias concluídos" valor={`${dia} / ${duracaoDias}`} />
        <Indicador
          rotulo="Peso atual"
          valor={ultima ? `${Number(ultima.peso_kg).toFixed(1)} kg` : '—'}
        />
        <Indicador rotulo="IMC" valor={ultima ? Number(ultima.imc).toFixed(1) : '—'} />
        <Indicador
          rotulo="Meta de água"
          valor={ultima ? `${(ultima.agua_recomendada_ml / 1000).toFixed(1)}L` : '—'}
        />
      </div>

      {primeira && ultima && medidas.length > 1 && (
        <Cartao className="bg-verde/10 text-center">
          <p className="font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
            Desde o início
          </p>
          <p className="font-titulo text-2xl font-bold text-verde">
            {(Number(ultima.peso_kg) - Number(primeira.peso_kg)).toFixed(1)} kg
          </p>
        </Cartao>
      )}

      <SecaoFotos matriculaId={matricula.id} userId={matricula.user_id} dia={dia} />
      <SecaoLembretes matriculaId={matricula.id} />
      <SecaoInstalacao />

      <section className="flex flex-col gap-2">
        <Botao variante="contorno" tamanho="bloco" onClick={() => void sair()}>
          Sair da conta
        </Botao>
        <p className="px-2 text-center text-xs leading-relaxed text-[var(--tinta-apagada)]">
          Para pedir a exclusão dos seus dados, fale com a equipe do Projeto Viver.
        </p>
      </section>
    </div>
  )
}

function Indicador({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="cartao p-4">
      <p className="text-xs text-[var(--tinta-apagada)]">{rotulo}</p>
      <p className="mt-0.5 font-titulo text-xl font-bold">{valor}</p>
    </div>
  )
}

// ---------------------------------------------------------------------
// Fotos antes / depois
// ---------------------------------------------------------------------

/** Ciclo 4 começa no dia 22 (seção 5). */
const DIA_INICIO_CICLO_4 = 22

function SecaoFotos({
  matriculaId,
  userId,
  dia,
}: {
  matriculaId: string
  userId: string
  dia: number
}) {
  const clienteQuery = useQueryClient()
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const chave = ['fotos', matriculaId]

  const { data, isLoading } = useQuery({
    queryKey: chave,
    queryFn: async () => {
      const { data: fotos, error } = await supabase
        .from('fotos')
        .select('*')
        .eq('matricula_id', matriculaId)

      if (error) throw error

      // Bucket privado: cada exibição usa um link assinado de vida curta.
      const comUrl = await Promise.all(
        (fotos as Foto[]).map(async (foto) => ({
          ...foto,
          url: await urlAssinada('fotos', foto.storage_path).catch(() => null),
        })),
      )

      return comUrl
    },
    staleTime: 8 * 60 * 1000,
  })

  async function enviarFotoDepois(arquivo: File) {
    setEnviando(true)
    setErro(null)
    try {
      const comprimida = await comprimirImagem(arquivo)
      const caminho = `${userId}/depois.webp`

      const { error: erroUpload } = await supabase.storage
        .from('fotos')
        .upload(caminho, comprimida, { upsert: true, contentType: 'image/webp' })
      if (erroUpload) throw erroUpload

      // onConflict explícito é obrigatório: sem ele, o upsert usa a
      // chave primária como alvo (sempre nova aqui), não a constraint
      // unique(matricula_id, tipo) — mesmo bug corrigido no onboarding.
      const { error: erroFoto } = await supabase
        .from('fotos')
        .upsert(
          { matricula_id: matriculaId, tipo: 'depois', storage_path: caminho },
          { onConflict: 'matricula_id,tipo' },
        )
      if (erroFoto) throw erroFoto

      void clienteQuery.invalidateQueries({ queryKey: chave })
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui enviar a foto. Tente de novo.'))
    } finally {
      setEnviando(false)
    }
  }

  if (isLoading) return <Esqueleto className="h-48" />

  const antes = data?.find((f) => f.tipo === 'antes')
  const depois = data?.find((f) => f.tipo === 'depois')
  // Liberado a partir do Ciclo 4 — antes disso ainda não há o que comparar.
  const podeEnviarDepois = dia >= DIA_INICIO_CICLO_4 && !depois

  return (
    <section>
      <TituloSecao>Antes e depois</TituloSecao>
      <div className="grid grid-cols-2 gap-3">
        {[
          { tipo: 'antes' as const, rotulo: 'Antes', foto: antes, envio: false },
          { tipo: 'depois' as const, rotulo: 'Depois', foto: depois, envio: podeEnviarDepois },
        ].map(({ tipo, rotulo, foto, envio }) => {
          const conteudoSlot = foto?.url ? (
            <img
              src={foto.url}
              alt={`Sua foto de ${rotulo.toLowerCase()}`}
              className="size-full object-cover"
            />
          ) : envio ? (
            <span className="flex flex-col items-center gap-1 text-[var(--tinta-apagada)]">
              <span className="text-2xl" aria-hidden>
                {enviando ? '⏳' : '📷'}
              </span>
              <span className="px-2 text-center text-[11px] leading-tight font-medium">
                {enviando ? 'Enviando…' : 'Toque para enviar'}
              </span>
            </span>
          ) : (
            <div className="flex size-full items-center justify-center text-2xl opacity-40">📷</div>
          )

          return (
            <div key={tipo}>
              {envio ? (
                <label className="block aspect-3/4 cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed border-coral/50 bg-[var(--cartao)]">
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={enviando}
                    onChange={(e) => {
                      const arquivo = e.target.files?.[0]
                      e.target.value = ''
                      if (arquivo) void enviarFotoDepois(arquivo)
                    }}
                  />
                  <div className="flex size-full items-center justify-center">{conteudoSlot}</div>
                </label>
              ) : (
                <div className="flex aspect-3/4 items-center justify-center overflow-hidden rounded-2xl bg-[var(--cartao)]">
                  {conteudoSlot}
                </div>
              )}

              <p className="mt-1 text-center text-xs font-medium text-[var(--tinta-apagada)]">
                {rotulo}
                {tipo === 'depois' && !foto && dia < DIA_INICIO_CICLO_4 && ' · no Ciclo 4'}
              </p>
            </div>
          )
        })}
      </div>

      {erro && (
        <p role="alert" className="mt-2 text-center text-xs text-pink">
          {erro}
        </p>
      )}

      <p className="mt-2 text-center text-xs text-[var(--tinta-apagada)]">
        Só você tem acesso a estas fotos.
      </p>
    </section>
  )
}

// ---------------------------------------------------------------------
// Lembretes
//
// Existe porque a web não tem agendamento local: os horários vivem no
// banco e são o que o cron consulta a cada 5 minutos. Mudar aqui muda
// o disparo do servidor.
// ---------------------------------------------------------------------

function SecaoLembretes({ matriculaId }: { matriculaId: string }) {
  const clienteQuery = useQueryClient()
  const [desligando, setDesligando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const chave = CHAVES.lembretes(matriculaId)

  const { data: lembretes = [], isLoading } = useQuery({
    queryKey: chave,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lembretes')
        .select('*, modelo:lembretes_modelo(*)')
        .eq('matricula_id', matriculaId)

      if (error) throw error
      return (data as LembreteComModelo[]).sort((a, b) => a.modelo.ordem - b.modelo.ordem)
    },
  })

  const atualizar = useMutation({
    mutationFn: async ({
      id,
      campos,
    }: {
      id: string
      campos: { horario?: string; ativo?: boolean }
    }) => {
      const { error } = await supabase.from('lembretes').update(campos).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      setErro(null)
      void clienteQuery.invalidateQueries({ queryKey: chave })
    },
    // Sem isto, uma gravação que falhasse era invisível: o campo
    // simplesmente voltava ao valor antigo no próximo refetch, sem
    // nenhuma explicação de por quê.
    onError: (e) => {
      setErro(mensagemDeErro(e, 'Não consegui salvar. Tente de novo.'))
      void clienteQuery.invalidateQueries({ queryKey: chave })
    },
  })

  if (isLoading) return <Esqueleto className="h-64" />

  return (
    <section>
      <TituloSecao>Seus lembretes</TituloSecao>

      {erro && (
        <p role="alert" className="mb-2 rounded-xl bg-pink/10 p-3 text-sm text-pink">
          {erro}
        </p>
      )}

      <div className="flex flex-col gap-2">
        {lembretes.map((lembrete) => (
          <LinhaLembrete
            key={lembrete.id}
            lembrete={lembrete}
            aoAlternarAtivo={() =>
              atualizar.mutate({ id: lembrete.id, campos: { ativo: !lembrete.ativo } })
            }
            aoMudarHorario={(horario) => atualizar.mutate({ id: lembrete.id, campos: { horario } })}
          />
        ))}
      </div>

      <StatusNotificacoes desligando={desligando} setDesligando={setDesligando} />
    </section>
  )
}

/**
 * Estado da inscrição de push NESTE aparelho, com um jeito de tentar
 * de novo.
 *
 * Faltava isto: se a inscrição falhasse ou fosse pulada no onboarding
 * — que só roda uma vez —, não havia nenhum caminho para reativar
 * dentro do app. A única saída era desinstalar e reinstalar. Foi assim
 * que um iPhone de teste ficou sem nenhuma assinatura registrada, sem
 * ninguém perceber: o Perfil só oferecia "desligar", nunca "ligar".
 */
function StatusNotificacoes({
  desligando,
  setDesligando,
}: {
  desligando: boolean
  setDesligando: (v: boolean) => void
}) {
  const [permissao, setPermissao] = useState<NotificationPermission | 'indisponivel'>(
    permissaoAtual,
  )
  const [ativando, setAtivando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function ativar() {
    setAtivando(true)
    setErro(null)
    const resultado = await ativarNotificacoes()
    setAtivando(false)
    setPermissao(permissaoAtual())

    if (resultado.estado === 'negada') {
      setErro(
        'Notificação bloqueada para este app. Vá em Ajustes do aparelho › Notificações › Viver, e ative por lá.',
      )
    } else if (resultado.estado === 'precisa_instalar') {
      setErro('Abra o app pelo ícone na tela de início — pelo navegador, a permissão não funciona.')
    } else if (resultado.estado === 'sem_suporte') {
      setErro('Este navegador não suporta notificações.')
    } else if (resultado.estado === 'erro') {
      setErro(resultado.mensagem)
    }
  }

  if (permissao === 'granted') {
    return (
      <Botao
        variante="fantasma"
        tamanho="pequeno"
        className="mt-3 w-full"
        carregando={desligando}
        onClick={async () => {
          setDesligando(true)
          await desativarNotificacoes()
          setDesligando(false)
          setPermissao(permissaoAtual())
        }}
      >
        Desligar notificações neste aparelho
      </Botao>
    )
  }

  return (
    <div className="mt-3">
      <Botao tamanho="pequeno" className="w-full" carregando={ativando} onClick={ativar}>
        {permissao === 'denied' ? 'Ver como ativar notificações' : 'Ativar notificações neste aparelho'}
      </Botao>
      {erro && (
        <p role="alert" className="mt-2 text-center text-xs text-pink">
          {erro}
        </p>
      )}
    </div>
  )
}

/**
 * Uma linha de lembrete, com o horário como rascunho local.
 *
 * O `<input type="time">` do iOS dispara `onChange` a cada dígito
 * girado na roleta (hora, depois minuto), não só na confirmação. Ligar
 * isso direto a uma mutação — como estava antes — dispara várias
 * gravações em sequência rápida para o mesmo campo; se elas chegam fora
 * de ordem, a que "vence" no banco pode não ser a última que a
 * participante realmente queria. Aqui o valor só é gravado no blur
 * (dedo saiu do campo), então existe uma gravação só, com o valor final.
 */
function LinhaLembrete({
  lembrete,
  aoAlternarAtivo,
  aoMudarHorario,
}: {
  lembrete: LembreteComModelo
  aoAlternarAtivo: () => void
  aoMudarHorario: (horario: string) => void
}) {
  const [rascunho, setRascunho] = useState(() => formatarHorario(lembrete.horario))

  // Se o servidor confirmar um valor diferente (sucesso ou erro que
  // reverteu), o rascunho acompanha — sem isto o campo poderia continuar
  // mostrando um horário que na prática não foi salvo.
  useEffect(() => {
    setRascunho(formatarHorario(lembrete.horario))
  }, [lembrete.horario])

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl border-2 p-3 transition-colors',
        lembrete.ativo
          ? 'border-[var(--borda)] bg-[var(--cartao)]'
          : 'border-transparent bg-[var(--cartao)] opacity-55',
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={lembrete.ativo}
        aria-label={`${lembrete.ativo ? 'Desativar' : 'Ativar'} ${lembrete.modelo.titulo}`}
        onClick={aoAlternarAtivo}
        className={cn(
          // min-h-7 sobrepõe o min-height:44px global — ver o mesmo
          // ajuste em admin/SecaoLembretes.tsx.
          'relative h-7 min-h-7 w-12 shrink-0 rounded-full transition-colors',
          lembrete.ativo ? 'bg-verde' : 'bg-[var(--borda)]',
        )}
      >
        <span
          className={cn(
            // left-1 fixo — ver o mesmo ajuste em admin/SecaoLembretes.tsx.
            'absolute top-1 left-1 size-5 rounded-full bg-white transition-transform',
            lembrete.ativo ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </button>

      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-medium">{lembrete.modelo.titulo}</span>
        <span className="block truncate text-xs text-[var(--tinta-apagada)]">
          {lembrete.modelo.corpo}
        </span>
      </span>

      <input
        type="time"
        value={rascunho}
        disabled={!lembrete.ativo}
        onChange={(e) => setRascunho(e.target.value)}
        onBlur={() => {
          if (rascunho && rascunho !== formatarHorario(lembrete.horario)) {
            aoMudarHorario(`${rascunho}:00`)
          }
        }}
        aria-label={`Horário de ${lembrete.modelo.titulo}`}
        className="h-11 shrink-0 rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-2 font-titulo text-sm font-semibold"
      />
    </div>
  )
}

/**
 * Como instalar, sempre acessível — não só no onboarding.
 *
 * O gate de instalação só aparece uma vez, na largada. Se ela quiser
 * instalar num aparelho novo mais tarde, ou mostrar o passo a passo
 * para outra pessoa, não havia mais nenhum lugar no app com essas
 * instruções. Fica fechado por padrão para não competir com o resto do
 * Perfil.
 */
function SecaoInstalacao() {
  const [aberto, setAberto] = useState(false)
  const [plataforma] = useState<Plataforma>(detectarPlataforma)
  const jaInstalado = estaInstalado()

  return (
    <section>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="flex w-full items-center justify-between gap-3 rounded-2xl bg-[var(--cartao)] p-4 text-left shadow-[var(--shadow-suave)]"
      >
        <span className="font-titulo text-[15px] font-semibold">
          {jaInstalado ? 'Instalar em outro aparelho' : 'Como instalar o app'}
        </span>
        <span className={cn('text-[var(--tinta-apagada)] transition-transform', aberto && 'rotate-180')}>
          ▾
        </span>
      </button>

      {aberto && (
        <div className="mt-2 flex flex-col gap-2">
          {jaInstalado && (
            <p className="px-1 text-xs text-[var(--tinta-apagada)]">
              Este aparelho já está com o app instalado. Os passos abaixo servem para instalar em
              outro celular.
            </p>
          )}
          {plataforma === 'ios' && <PassosIOS />}
          {plataforma === 'android' && <PassosAndroidEstatico />}
          {plataforma === 'desktop' && <AvisoDesktop />}
        </div>
      )}
    </section>
  )
}
